import type {Issue} from "../../domain/entities/issue.entity";

/**
 * Tiny shared drag state for "My Organizer" (Anke, 2026-09-06): native HTML5
 * Drag&Drop's `dataTransfer` can only carry serializable strings, not a live
 * `Issue` entity — a module-level singleton holding the currently-dragged
 * Issue is simpler than round-tripping the whole entity through JSON and
 * re-resolving it on drop, and correct for this app's single-tab, single-
 * pointer drag model (there is never more than one drag in flight at once).
 *
 * `draggedIssue` is set on `dragstart` (source: `<my-organizer-assigned-
 * column>`'s rows) and read + cleared on `drop`/`dragend` (target: `<my-
 * organizer-label-column>`).
 */
export const MyOrganizerDragContext = {
    draggedIssue: null as Issue | null,
};

/**
 * Window-level custom event name a `<my-organizer-label-column>` dispatches
 * after successfully assigning/moving a dropped Issue to itself — an Issue
 * can only ever belong to ONE personal-label column at a time (Anke,
 * 2026-09-06: "ein issue kann nur genau einer Spalte zugewiesen werden"), so
 * `<my-organizer-assigned-column>` listens for this on `window` and removes
 * the Issue from its own list live, without a full reload. A plain window
 * event (like `routes.ts`'s `va.router.goto`) rather than a DI-resolved
 * RxJS Subject: these two components are siblings under the view with no
 * existing shared service, and there is only ever one such event to relay.
 *
 * `personalLabelUid` — the label the Issue now belongs to — was added
 * later (Anke, 2026-09-06: "Neue Box anlegen", multiple `<my-organizer-
 * label-column>` instances at once): every OTHER label column ALSO listens
 * for this event now, to remove the Issue from ITS OWN `_rows` if a Move
 * just took it away (see that component's own listener doc comment). A
 * column must be able to tell "this is about MY label, I already applied it
 * myself locally" apart from "this is about a DIFFERENT label, remove the
 * Issue from my own list" — `issueUid` alone can't distinguish those two
 * cases, since either way it's the SAME Issue.
 */
export const MY_ORGANIZER_ISSUE_LABELED_EVENT = 'my-organizer.issue-labeled';

export interface MyOrganizerIssueLabeledEventDetail {
    issueUid: string;
    personalLabelUid: string;
}

/**
 * The reverse of the above (Anke, 2026-09-06: "es soll auch möglich sein,
 * das Issue wieder zurück in die linke Spalte zu ziehen"): `<my-organizer-
 * assigned-column>` dispatches this after successfully clearing an Issue's
 * `personalLabel` (dragged back out of a `<my-organizer-label-column>`) —
 * every label column already holds its OWN `Issue` objects in `_issues`, so
 * the uid alone is enough for it to filter the matching row back out.
 */
export const MY_ORGANIZER_ISSUE_UNLABELED_EVENT = 'my-organizer.issue-unlabeled';

export interface MyOrganizerIssueUnlabeledEventDetail {
    issueUid: string;
}

/**
 * "Mein Kalender" (Anke, 2026-09-07): the date-cell analog of
 * `MY_ORGANIZER_ISSUE_LABELED_EVENT` above — dispatched by a `<my-organizer-
 * calendar-day>` after successfully setting a dropped Issue's `personalDate`
 * to itself. `<my-organizer-assigned-column>` listens for this (an Issue
 * that now has a date is no longer "unassigned", exactly like one that got a
 * label), and every OTHER `<my-organizer-calendar-day>` listens too, to drop
 * the Issue from its OWN local rows if a re-drag just moved it to a
 * DIFFERENT day (`personalDate`, not `personalLabelUid`, is the field that
 * tells a day cell "is this event about MY OWN date" vs. "a different one" —
 * see that component's own listener doc comment).
 */
export const MY_ORGANIZER_ISSUE_DATED_EVENT = 'my-organizer.issue-dated';

export interface MyOrganizerIssueDatedEventDetail {
    issueUid: string;
    personalDate: string; // ISO date, "YYYY-MM-DD" — see IssuePersonalData.personalDate's own doc comment.
}

/**
 * The reverse of the above (symmetric with `MY_ORGANIZER_ISSUE_UNLABELED_EVENT`):
 * `<my-organizer-assigned-column>` dispatches this after successfully
 * clearing an Issue's `personalDate` (dragged back out of a `<my-organizer-
 * calendar-day>` onto the inbox).
 */
export const MY_ORGANIZER_ISSUE_UNDATED_EVENT = 'my-organizer.issue-undated';

export interface MyOrganizerIssueUndatedEventDetail {
    issueUid: string;
}

/**
 * A `<my-organizer-label-column>` instance's OWN event, NOT a `window` event
 * like the ones above — dispatched on itself (`this.dispatchEvent(...)`)
 * after successfully deleting the personal label it renders (Anke,
 * 2026-09-09: "füge einen Button hinzu, um eine Box zu löschen"). The
 * PARENT view (`o2d-personal-organizer.view.ts`, the only thing that owns
 * the `_labels` array a column's removal must update) already has a direct
 * element reference to bind a listener on via its own `repeat()` template —
 * unlike the Issue events above, there is only ever ONE listener that could
 * ever care about this, so a bubbling `window` broadcast would be pure
 * overhead. Mirrors `dashboard-creator.component.ts`'s own `TileEventName`
 * pattern for the exact same "child tells its one parent it removed
 * itself" shape.
 */
export const MY_ORGANIZER_LABEL_DELETED_EVENT = 'my-organizer.label-deleted';

export interface MyOrganizerLabelDeletedEventDetail {
    labelUid: string;
}
